import { I18n } from '@aws-amplify/core';
import { Col, Form, Row, Skeleton, message } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { REPORT_NAME_KEYS } from '@/utils/constants';
import { CustomReportStepKeyDTO } from '@/components/ui/Inputs/types/response/customReport';
import { FileForCustomReport } from '@/components/ui/Inputs/types/response/file';
import { useApplicationContext } from '@/context/v1/Application/context';

import { useSetBeraStepKeyResult } from '@/hooks/useSetBeraStepKeyResult';
import { useUpdateBeraReport } from '@/hooks/useUpdateBeraReport';

import { useBeraReportStepsContext } from '@/components/views/BeraJob/BeraFormSteps/context';
import { useBeraJobStepsContext } from '@/components/views/BeraJob/context';
import {
	useBeraReport,
	useBeraSteps,
	useCreateBeraResult,
	useCustomReport,
	useTasks,
	useUpdateBeraJobScores
} from '@/components/views/BeraJob/hooks/hooks';
import {
	CreateBeraResultRequest,
	CreateBeraStepKeyResultRequest,
	UpdateBeraJobScoresRequest,
	UpdateBeraResultRequest
} from '@/components/views/BeraJob/hooks/types/request';
import { BeraJobResult } from '@/components/views/BeraJob/Results';
import { BeraResultService, TasksRpn } from '@/components/views/BeraJob/services/beraResultService';
import { BeraResult } from './BeraResult';
import { BeraStepKey } from './BeraStepKey';
import { Footer } from './Footer';
import { Informations } from './Informations';
import { StepsMenu } from './StepsMenu';

import * as S from './styles';

const { useForm } = Form;

const beraResultService = new BeraResultService();

interface BeraStepProps {
	file: FileForCustomReport;
}

export type BeraStepUrlParams = {
	bera_job_summary_id: string;
};

export type StepMetaProps = {
	key: number;
	id: string;
	title: string;
	step: CustomReportStepKeyDTO[];
	name: string;
	sequence: number;
};

type InitialValues = {
	step_key: boolean;
	stress_level_id: string;
	frequency_id: string;
	total_task_duration_id: string;
	job_element: string;
	task_rpn: number;
};

export type StepKeysInitialValues = {
	[key: string]: {
		empty_step: boolean;
		[key: string]: boolean | InitialValues;
	};
};

export function BeraStep({ file }: Readonly<BeraStepProps>) {
	const [form] = useForm();
	const { organization, company } = useApplicationContext();
	const { bera_job_summary_id } = useParams<BeraStepUrlParams>();
	const { clickableResultStep, currentActiveTabIndex, handleClickableResultStepChange, handleIsLoading } =
		useBeraReportStepsContext();

	const {
		tabsDisabled,
		beraJobSummary,
		beraJobSummaryId,
		showBeraJobResult,
		beraReportsStatus,
		beraCommonInformations,
		addPeddingFileId,
		removePeddingFileId,
		handleCurrentFileIdChange,
		handleFinishBeraReport,
		handleShowBeraJobResultChange
	} = useBeraJobStepsContext();

	const [currentStep, setCurrentStep] = useState<number>(0);
	const [beraReportId, setBeraReportId] = useState<string>('');
	const [anyCollapseChecked, setAnyCollapseChecked] = useState<boolean>(false);
	const [isFormReady, setIsFormReady] = useState<boolean>(false);
	const [currentBeraStepResultId, setCurrentBeraStepResultId] = useState<string | null>(null);
	const hasInitializedStepRef = useRef(false);

	const commonInformations = useMemo(() => {
		const hasCommonInfo = beraCommonInformations?.cycle_id;

		if (hasCommonInfo) {
			return beraCommonInformations;
		}

		return {
			cycle_id: beraJobSummary?.cycle_id,
			sector_id: beraJobSummary?.sector_id,
			workstation_id: beraJobSummary?.workstation_id,
			line_id: beraJobSummary?.line_id,
			evaluator_id: beraJobSummary?.bera_report?.[0]?.evaluator_id,
			operator_evaluated: beraJobSummary?.bera_report?.[0]?.operator_evaluated,
			work_center: beraJobSummary?.bera_report?.[0]?.work_center,
			company_id: company?.id,
			organization_id: organization?.id
		};
	}, [beraCommonInformations, beraJobSummary, company?.id, organization?.id]);

	const {
		isFetching: gettingReport,
		data: report,
		isError: reportError
	} = useBeraReport({
		bera_job_summary_id: bera_job_summary_id ?? beraJobSummaryId,
		organization_id: organization?.id,
		company_id: company.id,
		file_id: file.id
	});

	const { data: beraCustomReport } = useCustomReport({
		organization_id: organization?.id,
		company_id: company.id,
		reportName: REPORT_NAME_KEYS.BERA
	});

	const customReportId = file.CustomReportsFiles?.custom_report_id ?? beraCustomReport?.id;

	const {
		data: stepsList,
		isLoading: gettingBeraSteps,
		isError
	} = useBeraSteps({
		organization_id: organization?.id,
		company_id: company.id,
		custom_report_id: customReportId
	});

	const tasks = useTasks({
		organization_id: organization?.id,
		company_id: company.id,
		cycle_id: commonInformations?.cycle_id
	});

	const { mutateAsync: createBeraResult, isLoading: creatingBeraResult } = useCreateBeraResult();
	const { mutateAsync: updateBeraResult, isLoading: updatingBeraResult } = useUpdateBeraReport();
	const { mutateAsync: updateBeraJobScores, isLoading: updatingBeraJobScores } = useUpdateBeraJobScores();

	const { mutateAsync: setKeyResult, isLoading: settingKeyResult } = useSetBeraStepKeyResult();

	const loading =
		gettingBeraSteps || gettingReport || creatingBeraResult || updatingBeraResult || updatingBeraJobScores;

	const sortedStepsList = useMemo(
		() => (stepsList ?? []).slice().sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
		[stepsList]
	);

	const stepsMeta: StepMetaProps[] = useMemo(
		() =>
			sortedStepsList.map((step, index) => ({
				key: index,
				id: step.id,
				title: step.description,
				step: step.step_key,
				name: step.name,
				sequence: step.sequence ?? index
			})),
		[sortedStepsList]
	);

	const firstNonInformationIndex = useMemo(
		() => stepsMeta.findIndex((step) => step.name !== 'informations' && step.name !== 'result'),
		[stepsMeta]
	);

	function clampUiStep(targetStep: number) {
		const last = Math.max(stepsMeta.length - 1, 0);
		return Math.min(Math.max(targetStep, 0), last);
	}

	const sequenceToIndex = useCallback(
		(sequence?: number | null) => {
			if (sequence === null || sequence === undefined) return 0;
			const exactIndex = stepsMeta.findIndex((step) => step.sequence === sequence);
			if (exactIndex >= 0) return exactIndex;
			const nextIndex = stepsMeta.findIndex((step) => step.sequence > sequence);
			if (nextIndex >= 0) return nextIndex;
			return Math.max(stepsMeta.length - 1, 0);
		},
		[stepsMeta]
	);

	const indexToSequence = useCallback(
		(index: number) => stepsMeta[index]?.sequence ?? stepsMeta[0]?.sequence ?? 0,
		[stepsMeta]
	);

	async function persistStep(uiStep: number, id?: string) {
		const targetUi = clampUiStep(uiStep);
		const reportId = id ?? beraReportId;
		if (reportId) {
			const backendStep = indexToSequence(targetUi);
			const lastSequence = stepsMeta[stepsMeta.length - 1]?.sequence ?? backendStep;
			const body = {
				step: backendStep,
				id: reportId,
				organization_id: organization?.id,
				company_id: company.id
			};
			await updateBeraResult(body);
			if (backendStep === lastSequence) {
				removePeddingFileId(file.id);
			}
		}
		setCurrentStep(targetUi);
	}

	useEffect(() => {
		handleIsLoading(loading);
		handleCurrentFileIdChange(file.id);
	}, [loading, tabsDisabled, file.id]);

	function getReportStepSequence() {
		if (!report?.step) return;

		if (typeof report?.step === 'number') {
			return report.step;
		}

		return stepsMeta[0]?.sequence ?? 0;
	}

	useEffect(() => {
		if (report?.id && !beraReportId) {
			setBeraReportId(report.id);
		}
	}, [report?.id, beraReportId]);

	useEffect(() => {
		if (!stepsMeta.length) return;
		const lastStepIndex = Math.max(stepsMeta.length - 1, 0);
		const reportStepSequence = getReportStepSequence();
		const reportStep = sequenceToIndex(reportStepSequence);
		if (!hasInitializedStepRef.current) {
			const initialStep = report?.consolidated ? lastStepIndex : reportStep;
			setCurrentStep(initialStep);
			hasInitializedStepRef.current = true;
			return;
		}
		if (report?.consolidated) {
			setCurrentStep(lastStepIndex);
		}
	}, [report?.consolidated, report?.step, stepsMeta, sequenceToIndex]);

	useEffect(() => {
		if (!stepsMeta.length) return;
		const reportStepSequence = getReportStepSequence();
		const lastSequence = stepsMeta[stepsMeta.length - 1]?.sequence ?? 0;
		const shouldRemove = !!report?.consolidated || (reportStepSequence ?? 0) >= lastSequence;
		if (shouldRemove) {
			removePeddingFileId(file.id);
		} else {
			addPeddingFileId(file.id);
		}

		if (report || reportError) {
			const hasTaskId = report ? typeof report.task_id === 'string' && report.task_id.length > 0 : false;
			const hasTaskTime = report
				? typeof report.task_time === 'number' || typeof report.task_time === 'string'
				: false;
			const hasKnownInjury = report ? typeof report.has_known_injury === 'boolean' : false;
			const hasAttributes = (hasTaskId && hasTaskTime && hasKnownInjury) || reportError;
			setIsFormReady(hasAttributes);
		}
	}, [report, reportError, stepsMeta.length, removePeddingFileId, addPeddingFileId, file.id]);

	useEffect(() => {
		const clickableResult = beraReportsStatus?.find((report) => report.fileId === file.id)?.completed ?? false;
		handleClickableResultStepChange(clickableResult);
	}, [currentActiveTabIndex]);

	const handleCheckAnyCollapse = useCallback(
		async (checked: boolean) => {
			setAnyCollapseChecked(checked);
			if (!checked) {
				handleClickableResultStepChange(false);
			}
		},
		[handleClickableResultStepChange]
	);

	const handleAutoSaveStepKey = useCallback(
		async (stepId: string, stepKeyId: string, isEmpty: boolean = false) => {
			if (!beraReportId) return;
			if (settingKeyResult) return;

			let defaultValues = {
				stress_level_id: null,
				frequency_id: null,
				total_task_duration_id: null,
				job_element: '',
				task_rpn: 0
			};

			if (!isEmpty) {
				const formValues = form.getFieldValue([file.id]);
				const stepKeyData = formValues?.[stepId]?.[stepKeyId];
				if (stepKeyData?.step_key) {
					defaultValues = {
						stress_level_id: stepKeyData.stress_level_id,
						frequency_id: stepKeyData.frequency_id,
						total_task_duration_id: stepKeyData.total_task_duration_id,
						job_element: stepKeyData.job_element || '',
						task_rpn: stepKeyData.task_rpn || 0
					};
				}
			}

			const body = {
				...defaultValues,
				custom_report_step_key_id: stepKeyId,
				id: beraReportId,
				organization_id: organization?.id,
				company_id: company.id
			};

			const result = await setKeyResult(body);
			setCurrentBeraStepResultId(result.id);
		},
		[beraReportId, organization?.id, company.id, file.id, form, setKeyResult]
	);

	const handleFormValuesChange = useCallback(() => {
		handleFinishBeraReport(file.id, false);
	}, [handleFinishBeraReport, file.id]);

	const renderStepContent = useCallback(() => {
		const currentStepData = sortedStepsList?.[currentStep];
		if (!currentStepData) return null;

		if (currentStepData.name === 'informations') {
			return (
				<Informations
					key={currentStepData.id}
					file={file}
					tasks={tasks}
					cycleId={commonInformations.cycle_id}
				/>
			);
		}

		if (currentStepData.name === 'result') {
			return <BeraResult key={currentStepData.id} file={file} />;
		}

		return (
			<BeraStepKey
				file={file}
				key={currentStepData.id}
				stepId={currentStepData.id}
				stepKeys={currentStepData.step_key}
				stepDescription={currentStepData.description}
				beraReportId={beraReportId}
				onCheckAnyCollapse={handleCheckAnyCollapse}
				onAutoSave={handleAutoSaveStepKey}
				workstation_id={commonInformations.workstation_id}
				currentBeraStepResultId={currentBeraStepResultId}
			/>
		);
	}, [
		sortedStepsList,
		currentStep,
		file,
		tasks,
		commonInformations.cycle_id,
		beraReportId,
		handleCheckAnyCollapse,
		handleAutoSaveStepKey,
		commonInformations.workstation_id,
		currentBeraStepResultId
	]);

	const initialValues = useMemo(
		() => formatStepsInitialValues(),
		[
			report?.id,
			report?.consolidated,
			report?.task_id,
			report?.task_time,
			report?.task_time_format,
			report?.has_known_injury,
			report?.collection_date,
			report?.step_key_results?.length,
			stepsMeta.length,
			file.id
		]
	);

	function formatStepsInitialValues() {
		const stepKeysInitialValues = formatStepKeysInitialValues();

		const initialValues = {
			[file.id]: {
				file_id: report?.file_id,
				consolidated: report?.consolidated,
				task_id: report?.task_id,
				task_time: report?.task_time,
				task_time_format: report?.task_time_format ?? 'seconds',
				has_known_injury: report?.has_known_injury,
				collection_date: report?.collection_date ? moment(report.collection_date) : undefined,
				...stepKeysInitialValues
			}
		};

		return initialValues;
	}

	function formatStepKeysInitialValues() {
		return report?.step_key_results?.reduce((partialInitialValue, currentResult) => {
			const prevEmptyStep = partialInitialValue[currentResult.step_id]?.empty_step;
			const empty_step = !(currentResult?.stress_level_id || prevEmptyStep === false);
			return {
				...partialInitialValue,
				[currentResult.step_id]: {
					...partialInitialValue[currentResult.step_id],
					empty_step,
					[currentResult.step_key_id]: {
						step_key: !!currentResult?.stress_level_id,
						stress_level_id: currentResult?.stress_level_id,
						stress_level_score: currentResult?.stress_level_score,
						frequency_id: currentResult?.frequency_id,
						frequency_score: currentResult?.frequency_score,
						total_task_duration_id: currentResult?.total_task_duration_id,
						total_task_duration_score: currentResult?.total_task_duration_score,
						job_element: currentResult?.job_element,
						task_rpn: currentResult?.task_rpn
					}
				}
			};
		}, {} as StepKeysInitialValues);
	}

	const stepMenu = [...stepsMeta];

	if (isError || tasks.isError) {
		return (
			<Row justify="center">
				<Col span={24}>
					<span>{I18n.get('Oops... Something went wrong!')}</span>
				</Col>
			</Row>
		);
	}

	if (gettingBeraSteps) {
		return (
			<Row justify="center">
				<Col span={20}>
					<Skeleton active />
				</Col>
			</Row>
		);
	}

	async function handleCreateBeraReportClick() {
		if (currentStep > 0 && !anyCollapseChecked) {
			message.error(I18n.get('Select at least one option.'));
			return;
		}

		const validatedValues = await form.validateFields();
		if (validatedValues) {
			const formValues = form.getFieldValue([file.id]);
			if (formValues) {
				const body = {
					...formValues,
					...commonInformations
				};

				const createParams: CreateBeraResultRequest = {
					organization_id: organization?.id,
					company_id: company.id,
					operator_evaluated: body.operator_evaluated,
					work_center: body.work_center,
					comment: body.comment,
					evaluator_id: body.evaluator_id,
					task_time: String(body.task_time).includes(',')
						? +body.task_time.replace(',', '.')
						: body.task_time,
					task_time_format: body.task_time_format,
					has_known_injury: body.has_known_injury,
					collection_date: body.collection_date,
					task_id: body.task_id,
					file_id: file.id,
					sector_id: body.sector_id,
					workstation_id: body.workstation_id,
					bera_job_summary_id: bera_job_summary_id ?? beraJobSummaryId
				};

				if (!beraReportId) {
					const created = await createBeraReport(createParams);
					addPeddingFileId(file.id);
					if (created?.id) {
						setBeraReportId(created.id);
						const targetStep = firstNonInformationIndex >= 0 ? firstNonInformationIndex : 0;
						await persistStep(targetStep, created.id);
					}
					return;
				}

				const { mappedTasksRpn } = mapStepKeysResults(formValues);
				const { severity, exposure, vulnerability, rpn } = beraResultService.calculateRPN(
					mappedTasksRpn,
					body.has_known_injury
				);

				const updateParams: UpdateBeraResultRequest = {
					...createParams,
					id: beraReportId,
					severity,
					exposure,
					vulnerability,
					rpn
				};
				const updated = await updateBeraReport(updateParams);
				if (updated) {
					removePeddingFileId(file.id);
					const currentSequence = indexToSequence(currentStep);
					const nextStep = sequenceToIndex(currentSequence + 1);
					if (nextStep !== currentStep) {
						await persistStep(nextStep);
					}
					return;
				}
			}
		}
	}

	async function handleStepMenuClick(step: number) {
		const targetStep = clampUiStep(step);
		if (targetStep === currentStep) return;

		const isResultStep = targetStep === stepsMeta.length - 1;
		if (isResultStep && !clickableResultStep) return;

		if (targetStep > currentStep) {
			if (currentStep === 0) {
				const values = await form.validateFields();
				if (!values) return;
			}
			if (currentStep > 0 && !anyCollapseChecked) {
				message.error(I18n.get('Select at least one option.'));
				return;
			}
		}

		await persistStep(targetStep);
	}

	async function handlePreviousStep() {
		const currentSequence = indexToSequence(currentStep);
		const prevStep = sequenceToIndex(currentSequence - 1);
		if (prevStep === currentStep) return;

		await persistStep(prevStep);
		if (showBeraJobResult) {
			handleShowBeraJobResultChange(false);
		}
	}

	async function handleFinishBeraClick() {
		const beraJobId = bera_job_summary_id ?? beraJobSummaryId;
		const body: UpdateBeraJobScoresRequest = {
			organization_id: organization?.id,
			company_id: company.id,
			bera_job_summary_id: beraJobId
		};
		const updated = await updateBeraJobScores(body);

		if (updated) {
			handleShowBeraJobResultChange(true);
		}
	}

	async function handleNextStep() {
		const currentStepData = sortedStepsList?.[currentStep];
		if (!currentStepData) return;

		if (currentStep > 0 && !anyCollapseChecked) {
			message.error(I18n.get('Select at least one option.'));
			return;
		}

		const formValues = form.getFieldValue([file.id]);
		if (!formValues) return;

		const currentStepValues = formValues[currentStepData.id] || {};
		const savePromises: Promise<void>[] = [];

		const isEmptyStepChecked = currentStepValues.empty_step;
		const firstStepKeyId = currentStepData.step_key?.[0]?.id;
		if (isEmptyStepChecked && firstStepKeyId) {
			savePromises.push(handleAutoSaveStepKey(currentStepData.id, firstStepKeyId, true));
		} else {
			currentStepData.step_key?.forEach((stepKey) => {
				const stepKeyData = currentStepValues[stepKey.id];
				if (stepKeyData?.step_key) {
					savePromises.push(handleAutoSaveStepKey(currentStepData.id, stepKey.id));
				}
			});
		}

		if (savePromises.length) {
			await Promise.all(savePromises);
		}

		if (formValues) {
			const body = {
				...formValues,
				...commonInformations
			};

			if (beraReportId) {
				const { mappedTasksRpn } = mapStepKeysResults(formValues);
				const { severity, exposure, vulnerability, rpn } = beraResultService.calculateRPN(
					mappedTasksRpn,
					body.has_known_injury
				);

				const updateParams = {
					id: beraReportId,
					organization_id: organization?.id,
					company_id: company.id,
					severity,
					exposure,
					vulnerability,
					rpn
				};
				await updateBeraResult(updateParams);
			}
		}

		const validatedValues = await form.validateFields();
		if (!validatedValues) return;

		const currentSequence = indexToSequence(currentStep);
		const nextStep = sequenceToIndex(currentSequence + 1);
		if (nextStep === currentStep) return;

		await persistStep(nextStep);
		setCurrentBeraStepResultId(null);
	}

	function mapStepKeysResults(formValues: any) {
		const mappedStepKeysResults: CreateBeraStepKeyResultRequest[] = [];
		const { stress_level, frequency, total_task_duration }: TasksRpn = {
			stress_level: [],
			frequency: [],
			total_task_duration: []
		};

		for (const step in formValues) {
			const currentStep = formValues[step];
			if (currentStep?.empty_step !== undefined) {
				for (const step_key in currentStep) {
					const currentStepKey = currentStep[step_key];
					if (currentStepKey?.step_key !== undefined) {
						if (!currentStepKey?.step_key) {
							const emptyResult = {
								frequency_id: null,
								job_element: undefined,
								stress_level_id: null,
								task_rpn: 0,
								total_task_duration_id: null,
								custom_report_step_key_id: step_key
							};
							mappedStepKeysResults.push(emptyResult);
						}
						if (currentStepKey?.step_key) {
							const task_rpn = typeof currentStepKey.task_rpn === 'string' ? 0 : currentStepKey.task_rpn;
							setTasksRpnScores(currentStepKey, stress_level, frequency, total_task_duration);
							const mappedResults: CreateBeraStepKeyResultRequest = {
								...currentStepKey,
								task_rpn,
								custom_report_step_key_id: step_key
							};
							mappedStepKeysResults.push(mappedResults);
						}
					}
				}
			}
		}

		const mappedTasksRpn = { stress_level, frequency, total_task_duration };

		return { mappedStepKeysResults, mappedTasksRpn };
	}

	function setTasksRpnScores(
		currentStepKey: any,
		stress_level: number[],
		frequency: number[],
		total_task_duration: number[]
	) {
		const stress_level_score = currentStepKey.stress_level_score;
		if (stress_level_score) {
			stress_level.push(stress_level_score);
		}
		const frequency_score = currentStepKey.frequency_score;
		if (frequency_score) {
			frequency.push(frequency_score);
		}
		const total_task_duration_score = currentStepKey.total_task_duration_score;
		if (total_task_duration_score) {
			total_task_duration.push(total_task_duration_score);
		}
	}

	async function createBeraReport(body: CreateBeraResultRequest) {
		const createdBeraReport = await createBeraResult(body);
		if (createdBeraReport) {
			handleFinishBeraReport(file.id);
			handleClickableResultStepChange(true);
		}
		return createdBeraReport;
	}

	async function updateBeraReport(body: UpdateBeraResultRequest) {
		const updatedBeraReport = await updateBeraResult(body);
		if (updatedBeraReport) {
			handleFinishBeraReport(file.id);
			handleClickableResultStepChange(true);
		}
		return updatedBeraReport;
	}

	return (
		<Row gutter={[0, 20]} justify="center">
			<Col xs={24}>
				{showBeraJobResult ? (
					<BeraJobResult />
				) : (
					<Row gutter={[64, 0]} justify="center">
						<StepsMenu
							stepMenu={stepMenu}
							currentStep={currentStep}
							onStepMenuClick={handleStepMenuClick}
						/>
						<Col sm={currentStep === stepsMeta.length - 1 ? 22 : 18} style={{ padding: 0 }}>
							{!isFormReady ? (
								<Row justify="center">
									<Col span={20}>
										<Skeleton active />
									</Col>
								</Row>
							) : (
								<S.BeraReportForm
									initialValues={initialValues}
									onValuesChange={handleFormValuesChange}
									autoComplete="off"
									name="bera_report"
									form={form}
								>
									<Col xs={24} style={{ padding: 0 }}>
										{renderStepContent()}
									</Col>
								</S.BeraReportForm>
							)}
						</Col>
					</Row>
				)}
			</Col>
			<Col xs={24}>
				<Row justify="center" style={{ minWidth: '190px' }}>
					<Footer
						stepName={stepsMeta[currentStep]?.name}
						currentStep={currentStep}
						totalSteps={stepsMeta.length}
						onNextClick={handleNextStep}
						onPreviousClick={handlePreviousStep}
						disabledReturn={report?.consolidated}
						onFinishBeraClick={handleFinishBeraClick}
						onCreateBeraReportClick={handleCreateBeraReportClick}
					/>
				</Row>
			</Col>
		</Row>
	);
}
